import Constants from "expo-constants"

export interface ConfigBaseProps {
  persistNavigation: "always" | "dev" | "prod" | "never"
  catchErrors: "always" | "dev" | "prod" | "never"
  exitRoutes: string[]
  authHosts: string[]
  websocketDisconnectBuffer: number
  appEnv: "development" | "production"
}

export type PersistNavigationConfig = ConfigBaseProps["persistNavigation"]

/**
 * Utility function to access environment variables that were configured in app.config.ts
 * These include variables starting with EXPO_PUBLIC_ or LOCAL_
 */
export function getEnvExtraVar(key: string): string | undefined {
  const value = Constants.expoConfig?.extra?.env?.[key]
  if (__DEV__) {
    console.debug(`getEnvExtraVar:${key}`, value)
  }
  return value
}

/**
 * Get all environment variables that were configured in app.config.ts
 */
export function getAllEnvExtraVars(): Record<string, string> {
  return Constants.expoConfig?.extra?.env || {}
}

export const AUTH_HOSTS = {
  PROD: "prod.auth.sidekickplatform.com",
  PREPROD: "preprod.auth.sidekickplatform.com",
  INTRL: "intrl.auth.sidekickplatform.com",
  STAG: "stag.auth.sidekickplatform.com",
  TEST: "test.auth.sidekickplatform.com",
  DEV: "dev.auth.sidekickplatform.com",
  DEV2: "dev2.auth.sidekickplatform.com",
} as const

const BaseConfig: ConfigBaseProps = {
  // This feature is particularly useful in development mode, but
  // can be used in production as well if you prefer.
  persistNavigation: "dev",

  /**
   * Only enable if we're catching errors in the right environment
   */
  catchErrors: "always",

  /**
   * This is a list of all the route names that will exit the app if the back button
   * is pressed while in that screen. Only affects Android.
   */
  exitRoutes: ["Welcome"],

  authHosts: [
    AUTH_HOSTS.PROD,
    AUTH_HOSTS.PREPROD,
    AUTH_HOSTS.STAG,
    AUTH_HOSTS.TEST,
    AUTH_HOSTS.DEV,
  ],

  // This is the time in milliseconds that we will wait before
  // disconnecting the websocket connection.
  websocketDisconnectBuffer: __DEV__ ? 5000 : 30000,

  appEnv: Constants.expoConfig?.extra?.appEnv ?? "development",
}

export default BaseConfig
